import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ListCounter } from '@/components/ListCounter.tsx';
import { Spinner } from '@/components/core/Spinner';
import { cn } from '@/helpers/classnames';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const NEAR_END_THRESHOLD_PX = 400;

interface MainProps<T> {
  renderItem: (item: T) => ReactNode;
  count: number;
  countName?: string;
  topRightContent?: ReactNode;
  totalItems: T[];
  filteredItems: T[];
  className?: string;
  divClassName?: string;
  children?: ReactNode;
  getItemKey?: (item: T) => string | number;
  ariaLabel?: string;
  onVisibleItemsChange?: (items: T[]) => void;
  onNearEnd?: () => void;
  isLoadingMore?: boolean;
}

export const VirtualizedList = <T,>({
  renderItem,
  count,
  countName,
  topRightContent,
  totalItems,
  filteredItems,
  className,
  divClassName,
  children,
  getItemKey,
  ariaLabel,
  onVisibleItemsChange,
  onNearEnd,
  isLoadingMore,
}: MainProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { formatMessage } = useIntl();
  const isTablet = useBreakpoint(1024);
  const listLabel = ariaLabel ?? formatMessage({ defaultMessage: 'Lista' });

  const rowVirtualizer = useVirtualizer({
    count: count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    measureElement: (element) => {
      return (element?.getBoundingClientRect().height || 35) + 8;
    },
    getItemKey: getItemKey
      ? (index) => getItemKey(filteredItems[index])
      : undefined,
  });

  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const calculateHeight = () => {
      if (!containerRef.current) return;

      // This re-calculates the height of the container when the view goes from desktop to tablet/mobile
      const containerHeight = containerRef.current.clientHeight;
      const adjustedHeight = isTablet ? containerHeight - 1 : containerHeight;

      setHeight(adjustedHeight);
    };

    calculateHeight();

    const resizeObserver = new ResizeObserver(calculateHeight);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [isTablet]);

  const notifyVisibleItems = useCallback(() => {
    if (!onVisibleItemsChange) return;

    const virtualItems = rowVirtualizer.getVirtualItems();
    const visibleItems = virtualItems
      .map((virtualItem) => filteredItems[virtualItem.index])
      .filter(Boolean);

    if (visibleItems.length > 0) {
      onVisibleItemsChange(visibleItems);
    }
  }, [onVisibleItemsChange, rowVirtualizer, filteredItems]);

  const onNearEndRef = useRef(onNearEnd);
  onNearEndRef.current = onNearEnd;

  const checkNearEnd = useCallback(() => {
    if (!onNearEndRef.current || !parentRef.current || isLoadingMore) return;

    const { scrollHeight, scrollTop, clientHeight } = parentRef.current;
    if (scrollHeight - scrollTop - clientHeight < NEAR_END_THRESHOLD_PX) {
      onNearEndRef.current();
    }
  }, [isLoadingMore]);

  useEffect(() => {
    checkNearEnd();
  }, [checkNearEnd, count, height]);

  useEffect(() => {
    if (!parentRef.current) return;
    if (!onVisibleItemsChange && !onNearEnd) return;

    const handleScroll = () => {
      checkNearEnd();

      if (!onVisibleItemsChange) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        notifyVisibleItems();
      }, 300);
    };

    const scrollElement = parentRef.current;
    scrollElement.addEventListener('scroll', handleScroll);

    const initialTimeout = setTimeout(() => {
      notifyVisibleItems();
    }, 500);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      clearTimeout(initialTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notifyVisibleItems,
    checkNearEnd,
    onVisibleItemsChange,
    onNearEnd,
    height,
  ]);

  return (
    <div className="flex size-full min-h-0 flex-col md:flex-[4]">
      {countName || topRightContent ? (
        <div className="flex shrink-0 items-end">
          {countName && !onNearEnd ? (
            <ListCounter
              totalItems={totalItems}
              filteredItems={filteredItems}
              type={countName}
              className="mx-4 my-0 md:mx-8 md:pb-0"
            />
          ) : null}
          {topRightContent ? (
            <div className="my-0 ml-auto pt-4 md:mr-12">{topRightContent}</div>
          ) : null}
        </div>
      ) : null}
      <OnEmptyList empty={totalItems.length === 0} label={countName ?? ''} />
      <div
        ref={containerRef}
        className={cn('flex min-h-0 flex-1 flex-col', className)}
      >
        {height !== null && (
          <div className="relative" style={{ height: `${height}px` }}>
            <div
              ref={parentRef}
              className={cn(
                'flex size-full overflow-auto px-4 md:px-8',
                divClassName,
              )}
            >
              <ul
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
                className={cn('mb-4 flex w-full flex-col md:mb-8', className)}
                aria-label={listLabel}
              >
                <li
                  key={'main-content'}
                  data-index={-1}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(0px)`,
                  }}
                >
                  <div ref={childrenRef}>{children}</div>
                </li>

                {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                  <li
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: `${20}px`,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {renderItem(filteredItems[virtualRow.index])}
                  </li>
                ))}
              </ul>
            </div>
            {isLoadingMore ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-4">
                <Spinner className="size-8" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

interface OnEmptyListProps {
  empty: boolean;
  label: string;
}

const OnEmptyList = ({ empty = false, label }: OnEmptyListProps) => {
  if (!empty) return;

  return (
    <div className="p-8">
      <FormattedMessage
        defaultMessage="Det finns inga {name}."
        values={{ name: label }}
      />
    </div>
  );
};
